import { Component, effect, Inject, inject, signal, WritableSignal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MeetingListService } from '../../meeting-list.service';
import { PrivacyLevel, PrivacyOverride } from '@vendasta/meeting-analysis';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, firstValueFrom, of, shareReplay } from 'rxjs';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import { User } from '@vendasta/iamv2/lib/user/user';

interface DialogData {
  meetingBotId: string;
  selectedPrivacyLevel: PrivacyLevel;
  overriddenPrivacy: PrivacyOverride | null;
}
@Component({
  selector: 'meeting-analysis-override-visibility-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './override-visibility-dialog.component.html',
  styleUrl: './override-visibility-dialog.component.scss',
})
export class OverrideVisibilityDialogComponent {
  private meetingListService = inject(MeetingListService);
  private snackbarService = inject(SnackbarService);
  private iamService = inject(IAMService);
  private translateService = inject(TranslateService);

  readonly namespaceName$ = this.meetingListService.namespaceName$;

  protected readonly PrivacyLevel = PrivacyLevel;
  changingPrivacyLevel = false;

  overriddenPrivacy: WritableSignal<PrivacyOverride | null> = signal(null);
  userThatOverrodePrivacy: WritableSignal<User | null> = signal(null);

  meetingBotId: string;
  selectedPrivacyLevel: WritableSignal<PrivacyLevel | null> = signal(null);

  isPrivacyLevelOverridden = computed(() => {
    const privacyOverride = this.overriddenPrivacy(); 
    return privacyOverride?.privacyLevel === PrivacyLevel.PRIVACY_LEVEL_PRIVATE;
  });

  constructor(
    public dialogRef: MatDialogRef<OverrideVisibilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.meetingBotId = data.meetingBotId;
    this.overriddenPrivacy.set(data.overriddenPrivacy);
    this.selectedPrivacyLevel.set(data.selectedPrivacyLevel);

    effect(async () => {
      if (this.overriddenPrivacy()) {
        if (!this.overriddenPrivacy()?.userId) {
          this.userThatOverrodePrivacy.set(null);
          return;
        }
        const user = await firstValueFrom(this.iamService
            .getUser({ userId: this.overriddenPrivacy()?.userId } as UserIdentifier)
            .pipe(
              catchError(() => {
                this.snackbarService.openErrorSnack('COMMON.ERROR');
                return of(null);
              }),
              shareReplay(1)));
        if (user) {
          this.userThatOverrodePrivacy.set(user);
        }
      }
    });
  }

  changeSelectedPrivacyLevel(selectedPrivacyLevel: boolean): void {
    if (selectedPrivacyLevel) {
      this.selectedPrivacyLevel.set(PrivacyLevel.PRIVACY_LEVEL_PRIVATE);
    } else {
      this.selectedPrivacyLevel.set(PrivacyLevel.PRIVACY_LEVEL_UNSPECIFIED);
    }
  }

  async overridePrivacyLevel(): Promise<void> {
    if (!this.meetingBotId || this.selectedPrivacyLevel() === null) {
      return;
    }
    this.changingPrivacyLevel = true;
    try {
      const res = await firstValueFrom(
        this.meetingListService.updateMeetingBotVisibility(this.meetingBotId, this.selectedPrivacyLevel()!),
      );
      if (res?.privacyOverride) {
        this.overriddenPrivacy.set(res.privacyOverride);
        this.snackbarService.openSuccessSnack('VISIBILITY.SUCCESS_CHANGING');
        this.dialogRef.close(true);
      } else {
        this.snackbarService.openErrorSnack('VISIBILITY.ERROR_CHANGING');
        this.changingPrivacyLevel = false;
      }
    } catch (error) {
      this.snackbarService.openErrorSnack('VISIBILITY.ERROR_CHANGING');
      this.changingPrivacyLevel = false;
    }
  }

  getFormattedDisplayName(user: User | null): string {
    if (!user) {
      return this.translateService.instant('COMMON.UNKNOWN_USER');
    }

    if (!user.firstName && !user.lastName) {
      return user.email || this.translateService.instant('COMMON.UNKNOWN_USER');
    }

    let name = user.firstName || '';
    if (user.lastName) {
      if (name === '') {
        name = user.lastName;
      } else {
        name += ' ' + user.lastName;
      }
    }
    return name;
  }
}
